/**
 * V2 sync (Theme 10, from 7cc48382).
 *
 * Install global window.onerror + window.onunhandledrejection handlers
 * that forward to the telemetry endpoint when the current user is a
 * tester or super-admin. Idempotent — calling twice is a no-op.
 *
 * Caller passes a `getTelemetryEnabled` predicate so the handlers can
 * re-check on every event (rather than capturing the value at install
 * time, which would go stale across login / logout).
 *
 * Wire this up once at app boot from `main.tsx` (or wherever the auth
 * provider is mounted). It runs whether or not telemetry is currently
 * enabled — the predicate gates per-event.
 */
import { sendTelemetryEvent } from './sendTelemetryEvent'

let installed = false

export interface InstallOptions {
  /** Returns true when the current user is a tester / super-admin. */
  getTelemetryEnabled: () => boolean
  /** Returns the current session JWT (or null). */
  getToken: () => string | null
  /** Optional source override (defaults to 'spa'). */
  source?: string
}

export function installGlobalTelemetryHandlers(opts: InstallOptions): void {
  if (installed) return
  installed = true

  const source = opts.source ?? 'spa'

  // Synchronous JS errors (script errors, uncaught throws in event handlers).
  // The legacy onerror signature is preserved for compatibility — modern
  // browsers also fire window.addEventListener('error') but the older form
  // catches strictly more (script-tag errors etc.).
  const previousOnError = window.onerror
  window.onerror = (message, source_url, lineno, colno, error) => {
    if (opts.getTelemetryEnabled()) {
      sendTelemetryEvent(
        {
          level: 'error',
          message: typeof message === 'string' ? message : String(message),
          source,
          context: {
            sourceUrl: source_url,
            lineno,
            colno,
            stack: error?.stack,
            userAgent: navigator.userAgent,
            route: window.location.pathname,
          },
        },
        opts.getToken,
      )
    }
    // Preserve any prior handler (e.g. a dev tool that wants to log too).
    if (typeof previousOnError === 'function') {
      return previousOnError(message, source_url, lineno, colno, error)
    }
    return false
  }

  // Unhandled promise rejections — the async equivalent of the above.
  // React Query, fetch promises, async event handlers all surface here
  // when their rejection isn't .catch'd downstream.
  window.addEventListener('unhandledrejection', (event) => {
    if (!opts.getTelemetryEnabled()) return

    const reason = event.reason
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : 'Unhandled promise rejection'

    sendTelemetryEvent(
      {
        level: 'error',
        message,
        source,
        context: {
          stack: reason instanceof Error ? reason.stack : undefined,
          reasonType: reason instanceof Error ? 'Error' : typeof reason,
          userAgent: navigator.userAgent,
          route: window.location.pathname,
        },
      },
      opts.getToken,
    )
  })
}
